import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpService} from '@core/backend/common/api/http.service';
import {
    GeoJSONGeometry,
    GeospatialAssetPopupRequest,
    GeospatialAssetPopupResponse,
    GeospatialGroupPopupRequest,
    GeospatialGroupPopupResponse,
    GeospatialRequest,
    GeospatialResponse,
    SpatialCustomLayerResponse,
} from '../model/api';
import {Project, ProjectCreate, ProjectDto, ProjectPopupRequest, ProjectUpdate} from '../model/project';
import {map} from 'rxjs/operators';
import {LayerType, MapLayerDetails} from '../model/layers';
import {APIResponse} from '@core/interfaces/system/system-common';

@Injectable()
export class GeospatialViewerApi {
    private prefix = 'geospatial';
    private prefixProject = 'projects';

    constructor(private api: HttpService) {}

    // Core map data
    public getMapGeoData(studyId: String, req: GeospatialRequest): Observable<GeospatialResponse> {
        return this.api.post(`${this.prefix}/${studyId}`, req);
    }

    public getAssetPopupInfo(
        studyId: String,
        req: GeospatialAssetPopupRequest,
    ): Observable<GeospatialAssetPopupResponse> {
        return this.api.post(`${this.prefix}/details/asset/${studyId}`, req);
    }

    public getAssetGroupPopupInfo(
        studyId: String,
        req: GeospatialGroupPopupRequest,
    ): Observable<GeospatialGroupPopupResponse> {
        return this.api.post(`${this.prefix}/details/group/${studyId}`, req);
    }

    // Additional layers
    public getCustomMapLayer(code: string): Observable<SpatialCustomLayerResponse> {
        return this.api.get(`${this.prefix}/layers/custom/${code}`);
    }

    public getCustomMapLayerDetails(type: LayerType): Observable<MapLayerDetails[]> {
        return this.api.get(`${this.prefix}/layers/custom/${type}/details`);
    }

    // Project APIs
    public getProject(id): Observable<Project> {
        return this.api.get(`${this.prefixProject}/${id}`);
    }
    public createProject(req: ProjectCreate): Observable<APIResponse<Project>> {
        return this.api.post(`${this.prefixProject}/`, req);
    }
    public updateProject(req: ProjectUpdate): Observable<APIResponse<Project>> {
        return this.api.put(`${this.prefixProject}/${req.id}`, req);
    }
    public deleteProject(id): Observable<Project> {
        return this.api.delete(`${this.prefixProject}/${id}`);
    }
    public listProjects(filterQuery: string, pageSize?: number): Observable<Project[]> {
        const query = `?pageNumber=1&pageSize=${pageSize ? pageSize : 1000}` + (filterQuery ? `&${filterQuery}` : '');
        return this.api.get(`${this.prefixProject}/list${query}`).pipe(
            map((res) => {
                return res.items;
            }),
        );
    }
    public getProjectListDto(studyId: string, filterQuery: string, pageSize?: number): Observable<ProjectDto[]> {
        const sortQuery = '&sortBy=projectName&orderBy=asc';
        const query = `?pageNumber=1&pageSize=${pageSize}${sortQuery}` + (filterQuery ? `&${filterQuery}` : '');
        return this.api.get(`${this.prefixProject}/dto/list/study/${studyId}${query}`).pipe(
            map((res) => {
                return res.items;
            }),
        );
    }
    public getProjectPopupInfo(req: ProjectPopupRequest): Observable<ProjectDto> {
        return this.api.get(`${this.prefixProject}/dto/${req.projectId}/study/${req.studyId}`);
    }
    public downloadProjectDataCsv(projectId: number, studyId: string): Observable<Record<string, string>> {
        return this.api.get(`${this.prefixProject}/download/${projectId}/study/${studyId}`);
    }

    // Project support
    public queryAssetsInPolygon(studyId: String, geoJsonStr: string): Observable<{count: number}> {
        const req = {
            geoJsonGeometry: geoJsonStr,
        };
        return this.api.post(`${this.prefix}/${studyId}/count`, req);
    }
    public getSearchAssetList(req: string): Observable<any> {
        const query = `?assetId=${req}`;
        return this.api.get(`${this.prefix}/search/asset${query}`);
    }
    public getSearchAssetInfo(req: string): Observable<GeoJSONGeometry> {
        const query = `?assetId=${req}`;
        return this.api.get(`${this.prefix}/asset${query}`);
    }
}
